import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { useEntidadeContext } from '../../EntidadeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faSave } from '@fortawesome/free-solid-svg-icons';
import './Atestado.css'; // CSS para impressão

const AtestadoTab = ({ consultaId, paciente }) => {
    const { loggedInUser } = useAuthContext();
    const { currentEntidade } = useEntidadeContext();

    const [diasAfastamento, setDiasAfastamento] = useState('');
    const [cid, setCid] = useState('');
    const [textoAtestado, setTextoAtestado] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isFormDirty, setIsFormDirty] = useState(false);

    // Gera o texto padrão do atestado
    const generateDefaultText = useCallback(() => {
        const dias = diasAfastamento || '[número de dias]';
        const cidText = cid 
            ? ` por motivo de doença (CID: ${cid}).` 
            : `, por motivos de saúde.`;
        
        return `Atesto, para os devidos fins, que o(a) Sr(a). ${paciente?.nome || '[Nome do Paciente]'} esteve sob meus cuidados médicos nesta data, necessitando de ${dias} ${dias === '1' ? 'dia' : 'dias'} de afastamento de suas atividades laborais a partir desta data${cidText}`;
    }, [paciente?.nome, diasAfastamento, cid]);

    // Busca o atestado existente
    const fetchAtestado = useCallback(async () => {
        if (!consultaId) return;
        setIsLoading(true);
        try {
            // BACKEND: Endpoint para buscar o atestado de uma consulta
            const response = await axios.get(`/api/consultas/${consultaId}/atestado`);
            if (response.data && response.data.atestado) {
                const { dias, cid: loadedCid, texto } = JSON.parse(response.data.atestado.dados_atestado);
                setDiasAfastamento(dias || '');
                setCid(loadedCid || '');
                setTextoAtestado(texto || generateDefaultText());
            } else {
                setTextoAtestado(generateDefaultText());
            }
        } catch (err) {
            console.log("Nenhum atestado anterior encontrado para esta consulta.");
            setTextoAtestado(generateDefaultText());
        } finally {
            setIsLoading(false);
            setIsFormDirty(false);
        }
    }, [consultaId, generateDefaultText]);

    useEffect(() => {
        fetchAtestado();
    }, [fetchAtestado]);

    // Atualiza o texto do atestado quando os campos mudam
    useEffect(() => {
        setTextoAtestado(generateDefaultText());
    }, [diasAfastamento, cid, paciente?.nome, generateDefaultText]);

    // Salvar atestado
    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const dados_atestado = JSON.stringify({ dias: diasAfastamento, cid, texto: textoAtestado });
            // BACKEND: Endpoint para salvar/atualizar o atestado
            await axios.post(`/api/consultas/${consultaId}/atestado`, { dados_atestado });
            setIsFormDirty(false);
            alert('Atestado salvo com sucesso!');
        } catch (err) {
            setError("Erro ao salvar o atestado.");
        } finally {
            setIsSaving(false);
        }
    };

    // Imprimir
    const handlePrint = () => {
        window.print();
    };

    if (isLoading) {
        return <div className="text-center my-5"><Spinner animation="border" /></div>;
    }

    return (
        <>
            <Card.Header className="d-flex justify-content-between align-items-center no-print">
                <span>Gerador de Atestado Médico</span>
                <div>
                    <Button variant="outline-secondary" onClick={handlePrint} className="me-2">
                        <FontAwesomeIcon icon={faPrint} className="me-2" /> Imprimir
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving || !isFormDirty}>
                        {isSaving ? <Spinner as="span" size="sm" /> : <FontAwesomeIcon icon={faSave} />} Salvar
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                
                <div className="no-print mb-4">
                    <Row>
                        <Col md={4}>
                            <Form.Group controlId="diasAfastamento">
                                <Form.Label>Dias de Afastamento *</Form.Label>
                                <Form.Control 
                                    type="number" 
                                    value={diasAfastamento} 
                                    onChange={(e) => {
                                        setDiasAfastamento(e.target.value);
                                        setIsFormDirty(true);
                                    }}
                                    placeholder="Ex: 3"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="cid">
                                <Form.Label>CID (Opcional)</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={cid} 
                                    onChange={(e) => {
                                        setCid(e.target.value);
                                        setIsFormDirty(true);
                                    }}
                                    placeholder="Ex: J00"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <hr />
                    <Form.Group controlId="textoAtestado">
                        <Form.Label>Texto do Atestado (editável)</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={8} 
                            value={textoAtestado}
                            onChange={(e) => {
                                setTextoAtestado(e.target.value);
                                setIsFormDirty(true);
                            }}
                        />
                    </Form.Group>
                </div>

                <div id="printable-atestado" className="atestado-container">
                    <header className="atestado-header">
                        <h1>Atestado Médico</h1>
                    </header>

                    <main className="atestado-body">
                        <p>{textoAtestado}</p>
                    </main>

                    <footer className="atestado-footer">
                        <p>{currentEntidade?.cidade || '[Cidade]'}, {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}.</p>
                        <div className="assinatura-medico">
                            <div className="assinatura-linha"></div>
                            <p>{loggedInUser?.nome || '[Nome do Médico]'}</p>
                            <p>CRM: {loggedInUser?.crm || '[CRM do Médico]'}</p>
                        </div>
                    </footer>
                </div>
            </Card.Body>
        </>
    );
};

export default AtestadoTab;
